import { type FC, type PropsWithChildren } from 'react';

import { NbaFeedbackWrapperComponent } from '@eneco/next-best-action';
import { TrackingProvider } from '@eneco/tracking';

interface CmsEnhancerProps {
  componentName: string;
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Standard per-component enhancer for CMS-rendered components.
 *
 * Pass as `wrapComponent` to `CmsProvider` to add GTM tracking and NBA
 * feedback to every component rendered via `@eneco/cms` `Placeholder`:
 *
 * ```tsx
 * <CmsProvider adapter={adapter} page={page} components={components} wrapComponent={CmsEnhancer}>
 *   ...
 * </CmsProvider>
 * ```
 *
 * Responsibilities:
 * - `TrackingProvider` — wraps with a `data-scope` element and forwards GTM
 *   click/focus/blur events. Disables itself in edit mode automatically.
 * - `NbaFeedbackWrapperComponent` — intercepts clicks and sends NBA feedback
 *   when `params.nba_feedback` is set. No-ops when the param is absent or
 *   when there is no active NBA serving point.
 *
 * Error boundary handling is provided by `@eneco/cms` itself (applied outside
 * this enhancer), so crashes here are caught and logged automatically.
 */
export const CmsEnhancer: FC<PropsWithChildren<CmsEnhancerProps>> = ({ children, componentName, params }) => (
  <TrackingProvider scope={componentName}>
    <NbaFeedbackWrapperComponent nbaFeedback={String(params?.nba_feedback ?? '')}>
      {children}
    </NbaFeedbackWrapperComponent>
  </TrackingProvider>
);

/**
 * `CmsEnhancer` for a page shown in edit or preview mode
 * (`page.context.pageEditing`): the tracking element only. Sitecore's own
 * placeholder leaves the NBA feedback out there (`addComponentTracking` in
 * `Placeholder.tsx`), so an editor's click never sends feedback.
 */
export const CmsEditingEnhancer: FC<PropsWithChildren<CmsEnhancerProps>> = ({ children, componentName }) => (
  <TrackingProvider scope={componentName}>{children}</TrackingProvider>
);
